import { Button } from "@/components/ui/UIElements/Button";
import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Icon } from "../..";
import { usePaymentMethods } from "../../../../hooks";

const PaymentMethodsStep = ({
  isEditing = false,
  selectedPaymentMethods,
  onPaymentMethodToggle,
  priceLevel,
  onPriceLevelChange,
  isAddingPaymentMethod,
  addPaymentMethod,
  errors,
}) => {
  const { t } = useTranslation("stores");
  const { paymentMethods, isLoading } = usePaymentMethods();
  const paymethodsToInclude = [
    "cash",
    "card_delivery",
    "stripe_connect_link_finitless",
  ];

  const checkEnableState = (id) => {
    if (!isEditing) {
      return {
        isEnabled: selectedPaymentMethods.includes(id),
      };
    }
    const isEnabled = !!selectedPaymentMethods?.find(
      (paymethod) => paymethod.paymethod_id === id && paymethod.enabled,
    );
    const paymethod = selectedPaymentMethods?.find(
      (paymethod) => paymethod.paymethod_id === id,
    );
    return {
      isEnabled,
      paymethod,
    };
  };

  const handlePaymentMethodToggle = (id) => {
    if (!isEditing) {
      return onPaymentMethodToggle(id);
    }
    if (!checkEnableState(id).paymethod?.id) {
      addPaymentMethod({ paymethod_id: id });
      return;
    }
    onPaymentMethodToggle(
      checkEnableState(id).paymethod?.id,
      !checkEnableState(id).isEnabled,
    );
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Price Level */}
      <div data-field="price_level">
        <label className="text-foreground mb-3 block text-sm font-medium">
          Price Level
          <span className="text-destructive ml-1">*</span>
        </label>
        <div className="grid grid-cols-3 gap-2 sm:grid-cols-5">
          {[1, 2, 3, 4, 5].map((level) => (
            <Button
              variant="ghost"
              key={level}
              onClick={() => onPriceLevelChange(level.toString())}
              className={`rounded-lg border-2 p-3 transition-colors sm:p-4 ${
                priceLevel === level.toString()
                  ? "border-primary bg-primary/20 text-[var(--od-action-content)]"
                  : "border-border bg-secondary text-foreground hover:border-border"
              }`}
            >
              <div className="text-center">
                <div className="text-base font-bold sm:text-lg">
                  {"$".repeat(level)}
                </div>
                <div className="text-xs">Level {level}</div>
              </div>
            </Button>
          ))}
        </div>
        {errors?.price_level && (
          <p className="text-destructive mt-2 text-sm">{errors?.price_level}</p>
        )}
      </div>

      {/* Payment Methods */}
      <div data-field="paymentMethods">
        <label className="text-foreground mb-3 block text-sm font-medium">
          Payment Methods
          <span className="text-destructive ml-1">*</span>
        </label>
        {isLoading ? (
          <div className="flex items-center justify-center py-6 sm:py-8">
            <div className="border-primary h-6 w-6 animate-spin rounded-full border-b-2 sm:h-8 sm:w-8"></div>
            <span className="text-muted-foreground ml-2 text-sm sm:text-base">
              Loading payment methods...
            </span>
          </div>
        ) : paymentMethods?.length === 0 ? (
          <div className="py-6 text-center sm:py-8">
            <Icon
              name="AlertCircle"
              className="text-muted-foreground mx-auto mb-2 h-6 w-6 sm:h-8 sm:w-8"
            />
            <p className="text-muted-foreground text-sm sm:text-base">
              No payment methods available
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3">
            {paymentMethods
              ?.filter((method) => paymethodsToInclude.includes(method.gateway))
              .map((method) => {
                const isDisabledPaymethod = !method.enabled;
                return (
                  <Button
                    variant="ghost"
                    key={method.id}
                    onClick={() =>
                      !isAddingPaymentMethod &&
                      !isDisabledPaymethod &&
                      handlePaymentMethodToggle(method.id)
                    }
                    disabled={isDisabledPaymethod}
                    className={`rounded-lg border-2 p-3 text-left transition-colors sm:p-4 ${
                      isDisabledPaymethod
                        ? "border-border bg-card cursor-not-allowed opacity-50"
                        : `cursor-${isAddingPaymentMethod ? "not-allowed" : "pointer"} ${
                            checkEnableState(method.id).isEnabled
                              ? "border-primary bg-primary/20 text-[var(--od-action-content)]"
                              : "border-border bg-secondary text-foreground hover:border-border"
                          }`
                    }`}
                  >
                    <div className="flex items-center space-x-2 sm:space-x-3">
                      <div
                        className={`h-4 w-4 rounded border-2 ${
                          !isDisabledPaymethod &&
                          checkEnableState(method.id).isEnabled
                            ? "border-primary bg-primary"
                            : "border-border"
                        }`}
                      >
                        {!isDisabledPaymethod &&
                          checkEnableState(method.id).isEnabled && (
                            <div className="flex h-full w-full items-center justify-center">
                              <Icon
                                name="Check"
                                className="h-3 w-3 text-white"
                              />
                            </div>
                          )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-medium sm:text-base">
                          {method.name}
                        </div>
                        {isDisabledPaymethod ? (
                          <div className="text-muted-foreground mt-1 text-xs">
                            {t(
                              "creationForm.steps.paymentMethods.disabledInSettings",
                            )}
                          </div>
                        ) : (
                          method.description && (
                            <div className="text-muted-foreground mt-1 line-clamp-2 text-xs">
                              {method.description}
                            </div>
                          )
                        )}
                      </div>
                    </div>
                  </Button>
                );
              })}
            {!paymentMethods?.some(
              (method) => method.gateway === "stripe_connect_link_finitless",
            ) && (
              <Link
                to="/settings/online-payments"
                className="border-border hover:border-primary hover:bg-primary/10 flex items-center space-x-2 rounded-lg border-2 border-dashed p-3 text-left transition-colors sm:space-x-3 sm:p-4"
              >
                <div className="flex h-4 w-4 items-center justify-center">
                  <Icon
                    name="CreditCard"
                    className="text-muted-foreground h-4 w-4"
                  />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-foreground truncate text-sm font-medium sm:text-base">
                    {t("creationForm.steps.paymentMethods.onlinePayments")}
                  </div>
                  <div className="text-primary mt-1 text-xs">
                    {t("creationForm.steps.paymentMethods.activateInSettings")}
                  </div>
                </div>
              </Link>
            )}
          </div>
        )}
        {errors?.paymentMethods && (
          <p className="text-destructive mt-2 text-sm">
            {errors?.paymentMethods}
          </p>
        )}
      </div>
    </div>
  );
};

export default PaymentMethodsStep;
